
import { useState, useRef, useCallback } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Roboto', sans-serif; }

  .emi-page {
    background: #efefef;
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
  }

  .emi-container {
    width: 100%;
    max-width: 1260px;
  }

  .emi-heading {
    text-align: center;
    font-size: 2rem;
    font-weight: 700;
    color: #CF2230;
    margin-bottom: 32px;
  }

  /* ── Card shell ── */
  .emi-card {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 3px;
    display: flex;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  }

  /* ── Red sidebar (hidden on mobile) ── */
  .emi-red-bar {
    background: #CF2230;
    width: 76px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ── Main content area ── */
  .emi-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* ── Sliders row ── */
  .emi-top {
    padding: 26px 30px 22px 30px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 22px;
    border-bottom: 1px solid #e8e8e8;
  }

  /* ── Details row ── */
  .emi-bottom {
    padding: 20px 30px 26px 30px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 22px;
  }

  /* ── Car price column (hidden on mobile) ── */
  .emi-price-col {
    width: 185px;
    flex-shrink: 0;
    border-left: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  /* ── Car price (mobile strip, hidden on desktop) ── */
  .emi-price-strip {
    display: none;
    background: #f9f9f9;
    border-top: 1px solid #e8e8e8;
    padding: 14px 20px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .car-price-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .car-price-value {
    font-size: 1.75rem;
    font-weight: 900;
    color: #111;
    letter-spacing: -0.5px;
    white-space: nowrap;
  }

  /* ── Slider group ── */
  .sg-label {
    display: block;
    font-size: 0.67rem;
    font-weight: 500;
    color: #999;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  .sg-value {
    display: block;
    font-size: 1.15rem;
    font-weight: 700;
    color: #111;
    margin-bottom: 14px;
  }

  .custom-slider {
    position: relative;
    height: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    margin-bottom: 6px;
  }

  .custom-slider-track {
    position: absolute;
    left: 0; right: 0;
    height: 3px;
    border-radius: 2px;
    background: #d0d0d0;
  }

  .custom-slider-fill {
    height: 100%;
    background: #CF2230;
    border-radius: 2px;
    position: relative;
  }

  .custom-slider-thumb {
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #CF2230;
    box-shadow: 0 1px 6px rgba(207,34,48,0.45);
    cursor: grab;
    transition: transform 0.1s ease, box-shadow 0.1s ease;
  }

  .custom-slider-thumb.dragging,
  .custom-slider-thumb:active {
    cursor: grabbing;
    transform: translateY(-50%) scale(1.18);
    box-shadow: 0 2px 10px rgba(207,34,48,0.55);
  }

  .sg-hints {
    display: flex;
    justify-content: space-between;
    font-size: 0.66rem;
    color: #bbb;
  }

  /* ── Detail group ── */
  .dg-label {
    display: block;
    font-size: 0.67rem;
    font-weight: 500;
    color: #999;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .dg-value {
    font-size: 1.5rem;
    font-weight: 900;
    color: #111;
    letter-spacing: -0.5px;
  }

  /* ═══════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ═══════════════════════════════════════ */

  /* ── Tablet: ≤ 900px ── */
  @media (max-width: 900px) {
    .emi-heading { font-size: 1.6rem; margin-bottom: 24px; }

    .emi-red-bar { width: 54px; }

    /* sidebar price col → hide on tablet too, show strip */
    .emi-price-col { display: none; }
    .emi-price-strip { display: flex; }

    .emi-top {
      grid-template-columns: 1fr 1fr;
      padding: 20px 22px 18px;
      gap: 20px;
    }

    .emi-bottom {
      grid-template-columns: 1fr 1fr;
      padding: 16px 22px 20px;
      gap: 20px;
    }

    .dg-value { font-size: 1.25rem; }
    .car-price-value { font-size: 1.4rem; }
  }

  /* ── Mobile: ≤ 600px ── */
  @media (max-width: 600px) {
    .emi-page { padding: 28px 12px; }

    .emi-heading { font-size: 1.3rem; margin-bottom: 18px; }

    /* Remove red sidebar on mobile — show top bar instead */
    .emi-red-bar { display: none; }

    .emi-card {
      flex-direction: column;
      border-radius: 6px;
    }

    /* Red top bar replaces sidebar on mobile */
    .emi-card::before {
      content: '';
      display: block;
      background: #CF2230;
      height: 6px;
      width: 100%;
    }

    .emi-top {
      grid-template-columns: 1fr;
      padding: 18px 16px 14px;
      gap: 18px;
    }

    .emi-bottom {
      grid-template-columns: 1fr;
      padding: 14px 16px 18px;
      gap: 16px;
    }

    .emi-price-strip {
      padding: 12px 16px;
    }

    .sg-value { font-size: 1.05rem; margin-bottom: 10px; }

    .dg-value { font-size: 1.3rem; }

    .car-price-value { font-size: 1.25rem; }
    .car-price-label { margin-bottom: 0; font-size: 0.65rem; }

    /* Make slider thumb bigger for touch */
    .custom-slider-thumb {
      width: 20px;
      height: 20px;
      right: -10px;
    }
    .custom-slider { height: 22px; }
  }

  /* ── Small mobile: ≤ 380px ── */
  @media (max-width: 380px) {
    .emi-heading { font-size: 1.15rem; }
    .dg-value { font-size: 1.1rem; }
    .car-price-value { font-size: 1.1rem; }
    .sg-value { font-size: 0.95rem; }
  }
`;

function formatINR(val) {
  return "₹" + Number(Math.round(val)).toLocaleString("en-IN");
}

function calcEMI(principal, annualRate, years) {
  if (!principal || !annualRate || !years) return 0;
  const r = annualRate / 12 / 100;
  const n = years * 12;
  return Math.round((principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
}

function snap(value, step) {
  return Math.round(value / step) * step;
}

function CustomSlider({ value, min, max, step, onChange }) {
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const pct = ((value - min) / (max - min)) * 100;

  const getValueFromEvent = useCallback((clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const raw = min + ratio * (max - min);
    return Math.min(max, Math.max(min, snap(raw, step)));
  }, [min, max, step]);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    setIsDragging(true);
    onChange(getValueFromEvent(e.clientX));

    const onMove = (me) => { if (dragging.current) onChange(getValueFromEvent(me.clientX)); };
    const onUp = () => {
      dragging.current = false; setIsDragging(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [getValueFromEvent, onChange]);

  const onTouchStart = useCallback((e) => {
    dragging.current = true; setIsDragging(true);
    onChange(getValueFromEvent(e.touches[0].clientX));

    const onMove = (te) => { if (dragging.current) onChange(getValueFromEvent(te.touches[0].clientX)); };
    const onEnd = () => {
      dragging.current = false; setIsDragging(false);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
  }, [getValueFromEvent, onChange]);

  return (
    <div className="custom-slider" ref={trackRef} onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
      <div className="custom-slider-track">
        <div className="custom-slider-fill" style={{ width: `${pct}%` }}>
          <div className={`custom-slider-thumb${isDragging ? " dragging" : ""}`} />
        </div>
      </div>
    </div>
  );
}

function SliderGroup({ label, displayValue, value, min, max, step, onChange, hintFrom, hintTo }) {
  return (
    <div>
      <span className="sg-label">{label}</span>
      <span className="sg-value">{displayValue}</span>
      <CustomSlider value={value} min={min} max={max} step={step} onChange={onChange} />
      <div className="sg-hints">
        <span>{hintFrom}</span>
        <span>{hintTo}</span>
      </div>
    </div>
  );
}

const CalcIcon = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="4" width="36" height="40" rx="4" stroke="white" strokeWidth="2.5" fill="none"/>
    <rect x="12" y="13" width="6" height="6" rx="1" fill="white"/>
    <rect x="21" y="13" width="6" height="6" rx="1" fill="white"/>
    <rect x="30" y="13" width="6" height="6" rx="1" fill="white"/>
    <rect x="12" y="22" width="6" height="6" rx="1" fill="white"/>
    <rect x="21" y="22" width="6" height="6" rx="1" fill="white"/>
    <rect x="30" y="22" width="6" height="6" rx="1" fill="white"/>
    <rect x="12" y="31" width="6" height="6" rx="1" fill="white"/>
    <rect x="21" y="31" width="6" height="6" rx="1" fill="white"/>
    <rect x="30" y="31" width="6" height="6" rx="1" fill="white"/>
  </svg>
);

export default function EMICalculator() {
  const [loanAmount, setLoanAmount]     = useState(10000);
  const [interestRate, setInterestRate] = useState(10);
  const [tenure, setTenure]             = useState(4);

  const emi = calcEMI(loanAmount, interestRate, tenure);

  return (
    <>
      <style>{styles}</style>
      <div className="emi-page">
        <div className="emi-container">
          <h2 className="emi-heading">EMI Calculator</h2>

          <div className="emi-card">
            {/* Red sidebar — hidden on mobile */}
            <div className="emi-red-bar"><CalcIcon /></div>

            {/* Main */}
            <div className="emi-main">
              {/* Sliders */}
              <div className="emi-top">
                <SliderGroup
                  label="LOAN AMOUNT (₹)" displayValue={formatINR(loanAmount)}
                  value={loanAmount} min={10000} max={999000} step={1000}
                  onChange={setLoanAmount} hintFrom="from ₹10K" hintTo="to ₹9.99L"
                />
                <SliderGroup
                  label="INTEREST RATE (%) P.A" displayValue={`${interestRate}%`}
                  value={interestRate} min={7} max={15} step={0.5}
                  onChange={setInterestRate} hintFrom="from 7%" hintTo="to 15%"
                />
                <SliderGroup
                  label="TENURE (YEARS)" displayValue={`${tenure} Years`}
                  value={tenure} min={1} max={8} step={1}
                  onChange={setTenure} hintFrom="from 1 year" hintTo="to 8 years"
                />
              </div>

              {/* Details */}
              <div className="emi-bottom">
                <div>
                  <span className="dg-label">LOAN AMOUNT</span>
                  <div className="dg-value">{formatINR(loanAmount)}</div>
                </div>
                <div>
                  <span className="dg-label">DOWN PAYMENT</span>
                  <div className="dg-value">₹50,000</div>
                </div>
                <div>
                  <span className="dg-label">EMI AMOUNT</span>
                  <div className="dg-value">{formatINR(emi)}</div>
                </div>
              </div>

              {/* Car price — mobile/tablet strip (replaces sidebar col) */}
              <div className="emi-price-strip">
                <span className="car-price-label">CAR PRICE</span>
                <span className="car-price-value">₹38,79,800*</span>
              </div>
            </div>

            {/* Car price column — desktop only */}
            <div className="emi-price-col">
              <div className="car-price-label">CAR PRICE</div>
              <div className="car-price-value">₹41,04,800*</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}